import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import { CheckCircledIcon } from "@radix-ui/react-icons";
import axios from "axios";
import _ from "lodash";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { resetAllSyncedData } from "src/lib/resetSync";
import { removeSyncDate } from "src/lib/slices/syncSlice";
import { patchUser } from "src/lib/slices/userSlice";
import { syncPropertiesTranslations } from "src/lib/syncPropertiesTranslations";
import useSWR from "swr";
import style from "./GoogleLogin.module.css";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Separator } from "./ui/separator";

const GoogleIcon = () => (
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 48 48"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    style={{ display: "block" }}
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    ></path>
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    ></path>
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    ></path>
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    ></path>
    <path fill="none" d="M0 0h48v48H0z"></path>
  </svg>
);

const fetcher = (token) => async (url) => {
  const res = await fetch(url, { headers: { Authorization: token } });

  return res.json();
};

export default function GoogleLogin() {
  const dispatch = useDispatch();

  const user = useSelector((state: any) => state.user);

  const [dialogOpened, setDialogOpened] = useState(false);
  const [existingTag, setExistingTag] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [newToken, setNewToken] = useState("");

  const [dialogLoading, setDialogLoading] = useState(false);

  const {
    data: connectionStatus,
    error,
    isLoading,
    mutate,
  } = useSWR(
    `${process.env.REACT_APP_API_URL}/users/google-connection-status`,
    fetcher(user.token),
    {}
  );

  const postAccessToken = async (accessToken: string, data?: any) => {
    return await axios
      .post(
        `${process.env.REACT_APP_API_URL}/users/connect-google`,
        {
          accessToken: accessToken,
          ...(data ?? {}),
        },
        {
          headers: {
            Authorization: user.token,
          },
        }
      )
      .catch(() => {
        toast.error("Přihlášení se nezdařilo, zkuste to prosím znovu.");
      });
  };

  return (
    <>
      {isLoading && (
        <div style={{ color: "hsl(var(--muted-foreground))" }}>Načítání...</div>
      )}
      {_.isUndefined(connectionStatus) && error && (
        <div style={{ color: "hsl(var(--muted-foreground))" }}>
          Chyba při načítání stavu
        </div>
      )}
      {!_.isUndefined(connectionStatus) &&
        connectionStatus.connected === false && (
          <button
            className={style["gsi-material-button"]}
            onClick={(e) => {
              e.preventDefault();
              GoogleAuth.signIn()
                .then(async (googleUser) => {
                  const response = await postAccessToken(
                    googleUser.authentication.accessToken
                  );

                  if (!response) return;

                  if (response?.data?.status === "already-connected") {
                    setExistingTag(response.data.tag);
                    setDialogOpened(true);
                    setAccessToken(googleUser.authentication.accessToken);
                    setNewToken(response.data.token);
                    return;
                  }

                  mutate();
                })
                .catch((...errors) => {
                  console.log("google errors", errors);
                  toast.error(
                    "Přihlášení se nezdařilo, zkuste to prosím znovu."
                  );
                });
            }}
          >
            <div className={style["gsi-material-button-state"]}></div>
            <div className={style["gsi-material-button-content-wrapper"]}>
              <div className={style["gsi-material-button-icon"]}>
                <GoogleIcon />
              </div>
              <>
                <span className={style["gsi-material-button-contents"]}>
                  Přihlásit se pomocí Google
                </span>
                <span style={{ display: "none" }}>
                  Přihlásit se pomocí Google
                </span>
              </>
            </div>
          </button>
        )}
      {connectionStatus?.connected === true && (
        <div
          style={{
            color: "hsl(var(--muted-foreground))",
          }}
        >
          <CheckCircledIcon className="inline-block mr-2" />
          Google účet je již přidělený k tomuto profilu
        </div>
      )}
      <Dialog open={dialogOpened} onOpenChange={setDialogOpened}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Tento účet už je přidělený k jinému profilu
            </DialogTitle>
            <DialogDescription>
              Tento účet je již přidělený k profilu{" "}
              {existingTag ? (
                <>
                  s tagem <b>@{existingTag}</b>
                </>
              ) : (
                "bez tagu"
              )}
              . Chcete data stáhnout ze starého profilu nebo Google účet
              přidělit k aktuálnímu profilu?
            </DialogDescription>
          </DialogHeader>
          <div>
            <Button
              className="w-full"
              variant="outline"
              disabled={dialogLoading}
              onClick={async () => {
                setDialogLoading(true);
                const response = await postAccessToken(accessToken, {
                  replace: true,
                });

                setDialogLoading(false);

                if (!response) return;

                mutate();

                setDialogOpened(false);
              }}
            >
              Přidělit Google účet k aktuálnímu profilu
            </Button>
            <div
              style={{
                color: "hsl(var(--muted-foreground))",
                fontSize: "0.8rem",
                paddingTop: "0.5rem",
              }}
            >
              Starý profil již nebude možné pomocí Google účtu obnovit
            </div>
          </div>
          <Separator />
          <div>
            <Button
              className="w-full"
              disabled={dialogLoading}
              onClick={async () => {
                setDialogLoading(true);

                const response = await axios
                  .post<
                    | { error: string }
                    | { id: string; token: string; tag: string }
                  >(`${process.env.REACT_APP_API_URL}/users/get-user`, {
                    token: newToken,
                  })
                  .catch(() => {});

                setDialogLoading(false);

                if (!response || !response?.data || "error" in response.data) {
                  toast.error(`Nepodařilo se přenést data.`);
                  return;
                }

                resetAllSyncedData(dispatch);

                Object.keys(syncPropertiesTranslations).forEach((key) => {
                  dispatch(
                    removeSyncDate({
                      property: key,
                    })
                  );
                });

                dispatch(
                  patchUser({
                    token: response.data.token,
                    tag: response.data.tag,
                    id: response.data.id,
                  })
                );

                setDialogOpened(false);

                setTimeout(() => {
                  mutate();
                }, 0);
              }}
            >
              Stáhnout starý profil
            </Button>
            <div
              style={{
                color: "hsl(var(--muted-foreground))",
                fontSize: "0.8rem",
                paddingTop: "0.5rem",
              }}
            >
              Aktuální data budou smazána a nahrazena daty ze starého profilu
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
